import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { formatApiErrorDetail } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import GoogleButton from "@/components/GoogleButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, user, checked } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submittedRef = useRef(false);
  useEffect(() => {
    if (submittedRef.current || !checked || !user) return;
    if (user.role === "business") navigate("/dashboard", { replace: true });
    else if (user.role === "admin") navigate("/admin", { replace: true });
    else if (user.role === "client") navigate("/directory", { replace: true });
    else navigate("/onboarding", { replace: true });
  }, [checked, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      submittedRef.current = true;
      toast.success("¡Bienvenido!");
      if (u.role === "admin") navigate("/admin");
      else if (u.role === "business") navigate("/dashboard");
      else navigate("/directory");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!checked || (user && !submittedRef.current)) {
    return <div className="flex min-h-screen items-center justify-center bg-background" data-testid="auth-loader"><span className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary" /></div>;
  }

  return (
    <AuthShell>
      <h1 className="font-head text-3xl font-extrabold tracking-tight text-secondary">{t("login_title")}</h1>
      <div className="mt-8">
        <GoogleButton role="business" />
      </div>
      <div className="my-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
        <span className="h-px flex-1 bg-slate-200" /> o <span className="h-px flex-1 bg-slate-200" />
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("email")}</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 rounded-sm" data-testid="login-email-input" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("password")}</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 rounded-sm" data-testid="login-password-input" />
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-sm bg-primary hover:bg-orange-600" data-testid="login-submit-btn">
          {loading ? "…" : t("login_title")}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        {t("no_account")} <Link to="/register" className="font-semibold text-primary hover:underline" data-testid="go-register-link">{t("nav_register")}</Link>
      </p>
    </AuthShell>
  );
}
